/**
 * Timezone-aware helpers for the Schedule Planner / WeekView / DayView.
 *
 * The `shifts.start_time` / `end_time` columns are Postgres `timestamptz` and
 * arrive on the client as ISO-8601 UTC strings (e.g. "2026-04-22T09:00:00+00:00").
 *
 * Naively passing these to `new Date(iso).getHours()` / `isSameDay()` /
 * `format(..., 'h:mm a')` interprets them in the **browser's** timezone, which
 * causes shifts to appear on the wrong calendar day and at the wrong hour for
 * any user not on UTC. This module wraps `date-fns-tz` to compute everything
 * in the **company's** timezone instead, which is the source of truth that
 * the publish/roster RPCs operate against.
 */
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';
import { startOfWeek, endOfWeek } from 'date-fns';

const FALLBACK_TZ = 'UTC';

const safeTz = (tz?: string | null): string => {
  if (!tz) return FALLBACK_TZ;
  try {
    // Validate by attempting to format; throws RangeError on invalid IANA name.
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return tz;
  } catch {
    return FALLBACK_TZ;
  }
};

/**
 * Returns a Date object whose local-clock fields (getHours, getDate, …) match
 * the wall-clock time of the given UTC instant in the supplied timezone.
 * This lets existing code that calls `.getHours()` / `isSameDay(...)` keep
 * working without further refactoring.
 */
export const toCompanyDate = (iso: string, tz?: string | null): Date => {
  return toZonedTime(iso, safeTz(tz));
};

/**
 * Format a UTC ISO timestamp in the company's timezone using a date-fns
 * format string (e.g. "h:mm a", "MMM d").
 */
export const formatInCompanyTz = (
  iso: string,
  tz: string | null | undefined,
  pattern: string
): string => {
  return formatInTimeZone(iso, safeTz(tz), pattern);
};

/**
 * True when the company-tz calendar day of the given UTC ISO timestamp
 * matches the calendar day of the supplied reference Date (compared as
 * yyyy-MM-dd in the same timezone).
 */
export const isSameCompanyDay = (
  iso: string,
  reference: Date,
  tz: string | null | undefined
): boolean => {
  const zone = safeTz(tz);
  const a = formatInTimeZone(iso, zone, 'yyyy-MM-dd');
  const b = formatInTimeZone(reference, zone, 'yyyy-MM-dd');
  return a === b;
};

/**
 * Hour-of-day (0–23) of the given UTC ISO timestamp in the company's tz.
 */
export const getCompanyHour = (iso: string, tz: string | null | undefined): number => {
  return Number(formatInTimeZone(iso, safeTz(tz), 'H'));
};

/**
 * Minute (0–59) of the given UTC ISO timestamp in the company's tz.
 */
export const getCompanyMinute = (iso: string, tz: string | null | undefined): number => {
  return Number(formatInTimeZone(iso, safeTz(tz), 'm'));
};

/**
 * Build the start/end ISO strings for a calendar week as it is perceived
 * in the company's timezone. Used by the data-fetch hook so the query
 * window matches what the user sees on screen.
 */
export const getCompanyWeekRange = (
  reference: Date,
  tz: string | null | undefined
): { startISO: string; endISO: string } => {
  const zone = safeTz(tz);
  // Compute Sunday-anchored week boundaries in the *reference* date's local
  // sense, then re-interpret midnight as a wall clock in the company tz.
  const localWeekStart = startOfWeek(reference, { weekStartsOn: 0 });
  const localWeekEnd = endOfWeek(reference, { weekStartsOn: 0 });
  // Re-anchor to company-tz midnight / end-of-day:
  const ymd = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const startISO = fromZonedTime(`${ymd(localWeekStart)}T00:00:00`, zone).toISOString();
  const endISO = fromZonedTime(`${ymd(localWeekEnd)}T23:59:59.999`, zone).toISOString();
  return { startISO, endISO };
};
